/**
 * Statistiky živého streamu kamery (go2rtc / WebRTC).
 *
 * Nahrazuje původní `latency.ts`, který počítal latenci z `EXT-X-PROGRAM-DATE-TIME`
 * v HLS playlistu — po přechodu na WebRTC žádný playlist neexistuje. Zdrojem je
 * `RTCPeerConnection.getStats()`, konkrétně `inbound-rtp` report videa.
 *
 * Modul je čistý (žádné DOM API, žádné timery), aby šel unit-testovat — čtení
 * statistik a interval řeší `hooks/useWebrtcStats.ts`.
 */

/** Jak se obraz aktuálně přenáší. Pořadí odpovídá preferenci ve `video-rtc.js`. */
export type Transport = "webrtc" | "mse" | "hls" | "unknown";

/** Tvar `inbound-rtp` reportu, ze kterého čteme (jen pole, která nás zajímají). */
export interface InboundVideoReport {
  type?: string;
  kind?: string;
  mediaType?: string;
  /** Kumulativní součet zdržení jitter bufferu v SEKUNDÁCH. */
  jitterBufferDelay?: number;
  /** Počet snímků, přes které se ten součet nasčítal. */
  jitterBufferEmittedCount?: number;
  framesPerSecond?: number;
}

/** Nad tímhle je hodnota zjevně nesmysl (zaseknutý buffer, přetečený čítač). */
export const PLAUSIBLE_MAX_MS = 30_000;

/** Když měření není k dispozici, WebRTC se chová prakticky živě. */
export const WEBRTC_FALLBACK_MS = 200;

/**
 * Průměrné zdržení jitter bufferu v ms.
 *
 * `jitterBufferDelay` je kumulativní součet v sekundách, `jitterBufferEmittedCount`
 * počet snímků — podíl dá průměr na snímek. Samotná okamžitá hodnota v API není.
 * Vrací `null`, když ještě nedorazil ani jeden snímek nebo je výsledek nesmyslný.
 */
export function jitterDelayMs(report: InboundVideoReport | null | undefined): number | null {
  if (!report) return null;
  const { jitterBufferDelay: delay, jitterBufferEmittedCount: count } = report;
  if (typeof delay !== "number" || typeof count !== "number") return null;
  if (!Number.isFinite(delay) || !Number.isFinite(count) || count <= 0) return null;
  const ms = (delay / count) * 1000;
  if (ms < 0 || ms > PLAUSIBLE_MAX_MS) return null;
  return ms;
}

/**
 * Vytáhne z `RTCStatsReport` první příchozí video stopu.
 *
 * `RTCStatsReport` je Map-like a iteruje hodnoty; bere se cokoli iterovatelného,
 * aby šel modul testovat obyčejným polem. `mediaType` je starší alias pro `kind` —
 * některé prohlížeče pořád posílají jen jeden z nich.
 */
export function pickInboundVideo(reports: Iterable<InboundVideoReport> | null | undefined): InboundVideoReport | null {
  if (!reports) return null;
  for (const r of reports) {
    if (r?.type !== "inbound-rtp") continue;
    const kind = r.kind ?? r.mediaType;
    if (kind === "video") return r;
  }
  return null;
}

/**
 * Odvodí aktivní přenos bez zásahu do vendorovaného `video-rtc.js`.
 *
 * Element zkouší webrtc a mse paralelně a nechá běžet ten, co dřív dodá obraz.
 * Navázané peer connection je tedy spolehlivý příznak, že vyhrál WebRTC;
 * neprázdné `mseCodecs` znamená, že běží MSE-over-WebSocket; jinak zbývá HLS.
 */
export function resolveTransport(
  connectionState: RTCPeerConnectionState | null | undefined,
  mseCodecs: string | null | undefined,
  hasVideo = true
): Transport {
  if (connectionState === "connected") return "webrtc";
  if (mseCodecs) return "mse";
  return hasVideo ? "hls" : "unknown";
}

/** Sekundy s jednou desetinou (česká čárka), nad 10 s celé sekundy. */
export function formatLatency(ms: number): string {
  const s = ms / 1000;
  if (s < 1) return `${Math.round(ms)} ms`;
  if (s < 10) return `${s.toFixed(1).replace(".", ",")} s`;
  return `${Math.round(s)} s`;
}

const TRANSPORT_NAME: Record<Transport, string> = {
  webrtc: "WebRTC",
  mse: "MSE",
  hls: "HLS",
  unknown: "—",
};

/**
 * Popisek do chipu: „WebRTC · 180 ms". Bez naměřené hodnoty jen název přenosu —
 * lepší než vymyšlené číslo, protože právě to bylo na původním chipu špatně
 * (natvrdo 200 ms bez ohledu na realitu).
 */
export function transportLabel(transport: Transport, latencyMs: number | null): string {
  const name = TRANSPORT_NAME[transport];
  return latencyMs === null ? name : `${name} · ${formatLatency(latencyMs)}`;
}
